import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, MapPin, Calendar, Package, FileText, MessageSquare,
  Clock, Truck, CheckCircle2, AlertCircle, Edit, Phone
} from 'lucide-react';
import api from '../../utils/api';

const STATUS_STEPS = [
  { key: 'draft', label: 'Draft', icon: FileText },
  { key: 'pending_estimate', label: 'Estimate Pending', icon: Clock },
  { key: 'estimate_sent', label: 'Estimate Sent', icon: FileText },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2 },
  { key: 'in_progress', label: 'In Progress', icon: Truck },
  { key: 'completed', label: 'Completed', icon: CheckCircle2 },
];

const CustomerJobDetailPage = ({ user }) => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const queryJobId = searchParams.get('jobId');
  const effectiveJobId = jobId || queryJobId;
  
  const [job, setJob] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (effectiveJobId) {
      fetchJob();
    } else {
      setLoading(false);
    }
  }, [effectiveJobId]);

  const fetchJob = async () => {
    try {
      const response = await api.get(`/jobs/${effectiveJobId}`);
      setJob(response.data);
      
      // Try to fetch estimate
      try {
        const estResponse = await api.get(`/estimates/job/${effectiveJobId}`);
        setEstimate(estResponse.data);
      } catch (e) {
        // No estimate yet
      }
    } catch (error) {
      console.error('Failed to load job:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIndex = (status) => {
    const index = STATUS_STEPS.findIndex(s => s.key === status);
    return index >= 0 ? index : 0;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'confirmed':
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'estimate_sent': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="animate-pulse mx-auto text-primary mb-4" size={48} />
          <p className="text-gray-500">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-4" data-testid="job-not-found">
        <header className="mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/dashboard')}>
            <ArrowLeft size={20} />
          </Button>
        </header>
        
        <Card className="text-center py-12">
          <CardContent>
            <AlertCircle size={64} className="mx-auto text-orange-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Job Not Found</h2>
            <p className="text-gray-500 mb-6">
              {effectiveJobId 
                ? "This job may have been deleted or you don't have access to it."
                : "No job ID provided."
              }
            </p>
            <Button onClick={() => navigate('/mobile/dashboard')}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusIndex = getStatusIndex(job.status);
  const progress = ((statusIndex + 1) / STATUS_STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="customer-job-detail">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/dashboard')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-lg font-bold">{job.job_number}</h1>
                <Badge className={getStatusColor(job.status)}>
                  {job.status?.replace(/_/g, ' ')}
                </Badge>
              </div>
            </div>
            {job.status === 'draft' && (
              <Button variant="outline" size="sm" onClick={() => navigate('/mobile/setup')}>
                <Edit size={16} className="mr-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Progress Tracker */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={progress} className="mb-4" />
            <div className="flex justify-between text-xs">
              {STATUS_STEPS.slice(0, 4).map((step, idx) => (
                <div 
                  key={step.key}
                  className={`text-center ${idx <= statusIndex ? 'text-primary font-medium' : 'text-gray-400'}`}
                >
                  <step.icon size={16} className="mx-auto mb-1" />
                  <span className="hidden sm:inline">{step.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Status Message */}
        {job.status === 'draft' && (
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Clock className="text-orange-600 mt-1" size={20} />
                <div>
                  <p className="font-medium text-orange-800">Awaiting Processing</p>
                  <p className="text-sm text-orange-700">
                    Your move details have been submitted. The company will review and send you an estimate soon.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {job.status === 'estimate_sent' && estimate && (
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <FileText className="text-purple-600 mt-1" size={20} />
                <div className="flex-1">
                  <p className="font-medium text-purple-800">Estimate Ready!</p>
                  <p className="text-sm text-purple-700 mb-3">
                    Your estimate is ready for review. Total: £{estimate.total_price?.toFixed(2)}
                  </p>
                  <Button size="sm" onClick={() => navigate(`/mobile/estimate?jobId=${job.id}`)}>
                    View Estimate
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Addresses */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin size={18} />
              Move Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-gray-500 font-medium">FROM</p>
              <p className="font-medium">{job.from_address?.formatted_address || job.from_address?.line1}</p>
              <p className="text-sm text-gray-600">
                {job.from_address?.city}, {job.from_address?.postcode}
              </p>
            </div>
            <div className="border-l-2 border-dashed border-primary h-4 ml-2" />
            <div>
              <p className="text-xs text-gray-500 font-medium">TO</p>
              <p className="font-medium">{job.to_address?.formatted_address || job.to_address?.line1}</p>
              <p className="text-sm text-gray-600">
                {job.to_address?.city}, {job.to_address?.postcode}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Dates */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar size={18} />
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {job.move_out_date && (
                <div>
                  <p className="text-xs text-gray-500">Move Out</p>
                  <p className="font-medium">{new Date(job.move_out_date).toLocaleDateString()}</p>
                </div>
              )}
              {job.move_in_date && (
                <div>
                  <p className="text-xs text-gray-500">Move In</p>
                  <p className="font-medium">{new Date(job.move_in_date).toLocaleDateString()}</p>
                </div>
              )}
            </div>
            {job.flexible_dates && (
              <p className="text-sm text-gray-500 mt-2">
                ✓ Flexible on dates
              </p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            className="h-14"
            onClick={() => navigate(`/mobile/inventory?jobId=${job.id}`)}
          >
            <Package size={20} className="mr-2" />
            <div className="text-left">
              <p className="font-medium">Inventory</p>
              <p className="text-xs text-gray-500">View/add items</p>
            </div>
          </Button>
          <Button 
            variant="outline" 
            className="h-14"
            onClick={() => navigate(`/mobile/chat?jobId=${job.id}`)}
          >
            <MessageSquare size={20} className="mr-2" />
            <div className="text-left">
              <p className="font-medium">Chat</p>
              <p className="text-xs text-gray-500">Message us</p>
            </div>
          </Button>
        </div>

        {/* Special Instructions */}
        {job.special_instructions && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Special Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700">{job.special_instructions}</p>
            </CardContent>
          </Card>
        )}

        {/* Contact Company */}
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-gray-600 mb-3">
              Need help? Contact your moving company directly.
            </p>
            <Button variant="outline" className="w-full">
              <Phone size={16} className="mr-2" />
              Contact Company
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CustomerJobDetailPage;
